import {
  Button,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
  makeStyles
} from '@material-ui/core'
import { ArrowDropDown, BugReport, DeveloperMode, PlayCircleOutline, Timeline } from '@material-ui/icons'
import React from 'react'
import { subscriptionMessageAppearanceActionAnalyticsPath } from '../../subscriptionPaths'
import { LARAVEL_ENDPOINT } from '../../env'

const INSTALLABLES_SERVER_LOGS_BASE = `${LARAVEL_ENDPOINT}/admin/errors/subscription`

const useStyles = makeStyles(theme => ({
  menuPaper: {
    maxWidth: 320,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    boxSizing: 'border-box'
  },
  menuItem: {
    whiteSpace: 'normal',
    alignItems: 'flex-start'
  },
  menuItemIcon: {
    minWidth: 32,
    paddingRight: theme.spacing(0.5),
    paddingTop: 6
  },
  listItemTextSecondary: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: 'block'
  }
}))

export interface SubscriptionSimulateControlsProps {
  subscriptionId: string
  configurationUrl?: string | null
  onSimulate: (simulatorIframeSrc: string) => void
  /** When there is no config URL. Defaults to 'No Config Page' body text. */
  noConfigFallback?: React.ReactNode
}

export const SubscriptionSimulateControls: React.FC<SubscriptionSimulateControlsProps> = ({
  subscriptionId,
  configurationUrl,
  onSimulate,
  noConfigFallback
}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClose = () => setAnchorEl(null)

  if (!subscriptionId && !configurationUrl) {
    return <>{noConfigFallback ?? <Typography variant='body2'>No Config Page</Typography>}</>
  }

  const laravelHref = subscriptionId ? `${INSTALLABLES_SERVER_LOGS_BASE}/${subscriptionId}` : undefined
  const eventAnalyticsTo = subscriptionId ? subscriptionMessageAppearanceActionAnalyticsPath(subscriptionId) : undefined

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        startIcon={<DeveloperMode fontSize='small' />}
        endIcon={<ArrowDropDown fontSize='small' />}
        onClick={e => setAnchorEl(e.currentTarget)}>
        Tools
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        classes={{ paper: classes.menuPaper }}>
        <ListSubheader disableSticky>Subscription Tools</ListSubheader>
        <MenuItem
          dense
          disabled={!configurationUrl}
          onClick={() => {
            if (configurationUrl) {
              onSimulate(`${configurationUrl}&platform=web`)
            }
            handleClose()
          }}>
          <ListItemIcon className={classes.menuItemIcon}>
            <PlayCircleOutline fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Simulate Setup' secondary='Make changes on behalf of the user' />
        </MenuItem>
        <MenuItem
          dense
          disabled={!laravelHref}
          component={laravelHref ? 'a' : 'div'}
          href={laravelHref}
          target='_blank'
          rel='noopener noreferrer'
          onClick={handleClose}>
          <ListItemIcon className={classes.menuItemIcon}>
            <BugReport fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Laravel Logs' secondary='Central scheduler info (subset)' />
        </MenuItem>
        <MenuItem
          dense
          disabled={!eventAnalyticsTo}
          component={eventAnalyticsTo ? 'a' : 'div'}
          href={eventAnalyticsTo}
          target='_blank'
          rel='noopener noreferrer'
          onClick={handleClose}>
          <ListItemIcon className={classes.menuItemIcon}>
            <Timeline fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Message Service History' secondary='Post-installable message tracking' />
        </MenuItem>
      </Menu>
    </>
  )
}
